import { useCallback, useMemo, useState } from 'react';

import type { NoteBody } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import type {
  ChipAttachment,
  RegistryEntry,
} from '@backrs_ui/grow-ui/src/redux/coachNoteEditor/coachNoteEditor';
import { MAX_UPLOAD_BYTES, uploadFile } from '@backrs_ui/grow-ui/src/utils/uploadFile';

import { countInlineImages } from './noteBody.transform';

export const MAX_ATTACHMENTS = 3;

export type UploadImageResult =
  | { ok: true; src: string; alt: string }
  | { ok: false; reason: 'too_large' | 'failed' };

export type UploadAttachmentResult = { ok: true } | { ok: false; reason: 'too_large' };

type UseNoteAttachmentsArgs = Readonly<{
  body: NoteBody | null;
  chips: readonly ChipAttachment[];
  onAddChip: (chip: ChipAttachment) => void;
  onUpdateChip: (chip: ChipAttachment) => void;
  onRemoveChip: (localId: string) => void;
  onAddRegistryEntry: (entry: RegistryEntry) => void;
}>;

export const useNoteAttachments = ({
  body,
  chips,
  onAddChip,
  onUpdateChip,
  onRemoveChip,
  onAddRegistryEntry,
}: UseNoteAttachmentsArgs) => {
  const [imageUploadCount, setImageUploadCount] = useState(0);

  const inlineCount = countInlineImages(body);
  const slotsUsed = chips.length + inlineCount;
  const slotsRemaining = Math.max(0, MAX_ATTACHMENTS - slotsUsed);

  const isUploading = imageUploadCount > 0 || chips.some((c) => c.status === 'uploading');

  const uploadImage = useCallback(
    async (file: File): Promise<UploadImageResult> => {
      if (file.size > MAX_UPLOAD_BYTES) return { ok: false, reason: 'too_large' };

      setImageUploadCount((n) => n + 1);

      try {
        const { mediaId, newDocumentUri } = await uploadFile({ file });

        onAddRegistryEntry([newDocumentUri, mediaId]);

        return { ok: true, src: newDocumentUri, alt: file.name };
      } catch (error) {
        console.error('CoachNote: image upload failed', { filename: file.name, error });

        return { ok: false, reason: 'failed' };
      } finally {
        setImageUploadCount((n) => n - 1);
      }
    },
    [onAddRegistryEntry],
  );

  const uploadAttachment = useCallback(
    async (file: File): Promise<UploadAttachmentResult> => {
      if (file.size > MAX_UPLOAD_BYTES) return { ok: false, reason: 'too_large' };

      const localId = crypto.randomUUID();
      const base = { localId, filename: file.name, size: file.size };

      onAddChip({ ...base, status: 'uploading' });

      try {
        const { mediaId } = await uploadFile({ file });

        onUpdateChip({ ...base, status: 'done', mediaId });
      } catch (error) {
        console.error('CoachNote: attachment upload failed', { filename: file.name, error });
        onUpdateChip({ ...base, status: 'error' });
      }

      return { ok: true };
    },
    [onAddChip, onUpdateChip],
  );

  return useMemo(
    () => ({
      chips,
      slotsUsed,
      slotsRemaining,
      isUploading,
      uploadImage,
      uploadAttachment,
      removeChip: onRemoveChip,
    }),
    [chips, slotsUsed, slotsRemaining, isUploading, uploadImage, uploadAttachment, onRemoveChip],
  );
};
